import { BaseApiService } from '@services/baseApiService';
import { api } from '@services/api';
import { ClientData, TokenValidationResponse, TokenResendResponse } from './types';
import { ApiError, ApiResponse } from '@/types/api';

class ClientService extends BaseApiService<ClientData> {
  constructor() {
    super('clients');
  }

  async create(data: ClientData) {
    return api.request<ClientData>(`${this.baseUrl}`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

    async update(id: string, data: Partial<any>): Promise<ApiResponse<any> | ApiError> {
      // Remove sensitive data if needed
      const userData = {
        ...data,
      };
      
      return await super.update(id, userData);
    }

  async validateToken(filledFormId: string, token: string) {
    return api.request<TokenValidationResponse>(`${this.baseUrl}/validate-token/`, {
      method: 'POST',
      headers: api.headers(),
      body: JSON.stringify({ filledFormId, token })
    });
  }

  async resendToken(filledFormId: string) {
    return api.request<TokenResendResponse>(`${this.baseUrl}/resend-token/`, {
      method: 'POST',
      headers: api.headers(),
      body: JSON.stringify({ filledFormId })
    });
  }

  async submitEmails(payload: { emails: string[]; phaseForm: string }): Promise<any> {
    try {
      const response = await api.request(`${this.baseUrl}/batch-email`, {
        method: 'POST',
        headers: api.headers(),
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error) {
      throw new Error('Failed to submit emails');
    }
  }

  async submitPhones(payload: { phones: string[]; phaseFormId: string, processId: string, productId: string }): Promise<any> {
    try {
      const response = await api.request(`${this.baseUrl}/batch-whatsapp`, {
        method: 'POST',
        headers: api.headers(),
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error) {
      throw new Error('Failed to submit phone numbers');
    }
  }
  
  /**
   * Envia um arquivo para um campo específico de um formulário preenchido
   * @param phaseFormId - ID do formulário de fase
   * @param processId - ID do processo
   * @param formFieldId - ID do campo do formulário
   * @param file - Arquivo a ser enviado
   * @param onProgress - Callback para acompanhar o progresso do upload
   * @returns Resposta da API com o caminho do arquivo
   */
  async uploadDocument(
    phaseFormId: string, 
    processId: string, 
    formFieldId: string, 
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<any> {
    // Validar o arquivo
    this.validateFile(file);
    
    const formData = new FormData();
    formData.append('file', file);
    
    const endpoint = `/filledforms/${phaseFormId}/${processId}/${formFieldId}/temp-upload`;
    
    try {
      const response = await api.uploadRequest<any>(
        endpoint,
        formData,
        onProgress
      );
      
      if ('data' in response) {
        return response.data;
      }
      
      throw new Error('Resposta inválida do servidor');
    } catch (error) {
      console.error('Erro no upload do documento:', error);
      if (error instanceof Error) {
        throw new Error(`Falha no upload: ${error.message}`);
      }
      throw error;
    }
  }
  
  /**
   * Valida o arquivo antes do upload
   * @param file Arquivo a ser validado
   */
  private validateFile(file: File): void {
    const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
    
    if (!file) {
      throw new Error('Nenhum arquivo fornecido');
    }

    if (file.size > MAX_FILE_SIZE) {
      throw new Error(`O arquivo é muito grande. O tamanho máximo permitido é ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
    }
  }
}

export const clientService = new ClientService(); 